import type { GuildWarRosterBoard } from "./types.js"

import { isMartialPathCode } from "./path-registry.js"
import { getGuildWarAssignedMemberIds } from "./roster-validation.js"

export const collectRosterBoardAssignedMemberIds = (
  board: GuildWarRosterBoard,
) => {
  const teamAssignedIds = getGuildWarAssignedMemberIds(board)
  const commanderIds = board.commanders.filter(
    (value): value is string => value !== null,
  )
  const halftimeIds = board.halftimePerformers.filter(
    (value): value is string => value !== null,
  )

  return new Set([...teamAssignedIds, ...commanderIds, ...halftimeIds])
}

export const pruneUnassignedOverrides = (
  board: GuildWarRosterBoard,
): GuildWarRosterBoard => {
  const assignedIds = collectRosterBoardAssignedMemberIds(board)
  const memberPaths = Object.fromEntries(
    Object.entries(board.memberPaths ?? {}).filter(([memberId]) =>
      assignedIds.has(memberId),
    ),
  )
  const memberDisplayNames = Object.fromEntries(
    Object.entries(board.memberDisplayNames ?? {}).filter(([memberId]) =>
      assignedIds.has(memberId),
    ),
  )

  return { ...board, memberPaths, memberDisplayNames }
}

export const validateRosterBoardMemberPaths = (
  board: GuildWarRosterBoard,
): string[] => {
  const formatPathCodeForIssue = (value: unknown) => {
    if (typeof value === "string") {
      return value
    }

    if (value === null) {
      return "null"
    }

    return JSON.stringify(value)
  }

  const assignedIds = collectRosterBoardAssignedMemberIds(board)
  const issues: string[] = []

  for (const memberId of Object.keys(board.memberPaths ?? {})) {
    if (!assignedIds.has(memberId)) {
      issues.push(`Path snapshot exists for unassigned member: ${memberId}`)
    }
  }

  for (const memberId of assignedIds) {
    const snapshot = board.memberPaths?.[memberId]
    if (!snapshot) {
      continue
    }

    const primaryPathCode = snapshot.primaryPathCode as unknown
    const secondaryPathCode = snapshot.secondaryPathCode as unknown

    if (
      primaryPathCode !== null &&
      (typeof primaryPathCode !== "string" ||
        !isMartialPathCode(primaryPathCode))
    ) {
      issues.push(
        `Invalid primary path code for ${memberId}: ${formatPathCodeForIssue(primaryPathCode)}`,
      )
    }

    if (
      secondaryPathCode !== null &&
      (typeof secondaryPathCode !== "string" ||
        !isMartialPathCode(secondaryPathCode))
    ) {
      issues.push(
        `Invalid secondary path code for ${memberId}: ${formatPathCodeForIssue(secondaryPathCode)}`,
      )
    }

    if (
      typeof primaryPathCode === "string" &&
      typeof secondaryPathCode === "string" &&
      isMartialPathCode(primaryPathCode) &&
      isMartialPathCode(secondaryPathCode) &&
      primaryPathCode === secondaryPathCode
    ) {
      issues.push(`Duplicate primary/secondary path for ${memberId}`)
    }
  }

  return issues
}
